import { Component } from 'react';
import { withTranslation } from 'react-i18next';

import ReactSelect from '../../../../components/ReactSelect';
import { GRID_SELECT } from '../../GscTools/selectStyles';
import { RULE_DIMENSIONS, RULE_OPERATORS, emptyRule, ruleError, ruleLabel } from '../scope';

/**
 * The report's filters: a name, a dimension, an operator and the literal or pattern to match.
 *
 * Nothing is stored in the account or in the browser; a filter travels with the applied scope in
 * the page's address, so a reloaded or shared report keeps it and nothing else does.
 *
 * Added rules are chips, each removable on its own: removing one restores the broader scope, and
 * because every rule is part of the request, that takes effect on the next Apply rather than
 * re-filtering rows already on screen. All rules are ANDed — Search Console's own "and" group
 * type — and no OR grouping is offered, because a row returned under an OR group answers a
 * different question than the one this report asks.
 */
class RuleEditor extends Component {
    constructor(props) {
        super(props);
        this.state = { draft: emptyRule(), error: null };

        this.handleAdd = this.handleAdd.bind(this);
    }

    patch(partial) {
        this.setState((state) => ({ draft: { ...state.draft, ...partial }, error: null }));
    }

    handleAdd(event) {
        event.preventDefault();

        const { t, rules, onChange } = this.props;
        const draft = { ...this.state.draft, expression: this.state.draft.expression.trim() };
        const error = ruleError(draft, t);

        if (error) return this.setState({ error });

        // A rule that is already in force is not added twice: the request would carry the same
        // filter twice over, which narrows nothing further and reads as a mistake in the export.
        const exists = rules.some(
            (rule) =>
                rule.dimension === draft.dimension &&
                rule.operator === draft.operator &&
                rule.expression === draft.expression,
        );
        if (exists) {
            return this.setState({
                error: t('tools.pages_queries_changes.rules.errors.duplicate'),
            });
        }

        onChange([...rules, draft]);
        this.setState({ draft: emptyRule(), error: null });
    }

    render() {
        const { t, rules, onChange } = this.props;
        const { draft, error } = this.state;

        const dimensionOptions = RULE_DIMENSIONS.map((value) => ({
            value,
            label: t(`tools.pages_queries_changes.rules.dimensions.${value}`),
        }));
        const operatorOptions = RULE_OPERATORS.map((value) => ({ value, label: value }));

        return (
            <div className="gsc-tool-rules">
                <div className="gsc-tool-rules-head">
                    <h4>{t('tools.pages_queries_changes.rules.title')}</h4>
                    <p>{t('tools.pages_queries_changes.rules.description')}</p>
                </div>

                <div className="gsc-tool-rules-grid">
                    <div className="gsc-tool-field">
                        <label htmlFor="pqc-rule-name">
                            {t('tools.pages_queries_changes.rules.name')}
                        </label>
                        <input
                            id="pqc-rule-name"
                            type="text"
                            className="form-control"
                            value={draft.name}
                            placeholder={t('tools.pages_queries_changes.rules.name_placeholder')}
                            onChange={(event) => this.patch({ name: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label>{t('tools.pages_queries_changes.rules.dimension')}</label>
                        <ReactSelect
                            options={dimensionOptions}
                            value={dimensionOptions.find((item) => item.value === draft.dimension)}
                            blurInputOnSelect={true}
                            classname="gsc-tool-select"
                            controlStyles={GRID_SELECT}
                            customeOnChange={(item) => this.patch({ dimension: item.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label>{t('tools.pages_queries_changes.rules.operator')}</label>
                        <ReactSelect
                            options={operatorOptions}
                            value={operatorOptions.find((item) => item.value === draft.operator)}
                            blurInputOnSelect={true}
                            classname="gsc-tool-select"
                            controlStyles={GRID_SELECT}
                            customeOnChange={(item) => this.patch({ operator: item.value })}
                        />
                    </div>

                    <div className="gsc-tool-field">
                        <label htmlFor="pqc-rule-expression">
                            {t('tools.pages_queries_changes.rules.expression')}
                        </label>
                        <input
                            id="pqc-rule-expression"
                            type="text"
                            className="form-control"
                            value={draft.expression}
                            placeholder={t(
                                'tools.pages_queries_changes.rules.expression_placeholder',
                            )}
                            onChange={(event) => this.patch({ expression: event.target.value })}
                        />
                    </div>

                    <div className="gsc-tool-rules-add">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleAdd}
                        >
                            {t('tools.pages_queries_changes.rules.add')}
                        </button>
                    </div>
                </div>

                {/* Under the grid rather than beneath the expression field: inside a cell the
                    note made that column taller than the rest and left the button floating
                    below the inputs it belongs beside. */}
                <p className="gsc-tool-rules-note">
                    {t('tools.pages_queries_changes.rules.regex_note')}
                </p>

                {!!error && <div className="gsc-tool-banner error">{error}</div>}

                {!rules.length ? (
                    <p className="gsc-tool-field-note">
                        {t('tools.pages_queries_changes.rules.none')}
                    </p>
                ) : (
                    <div className="gsc-tool-chips">
                        {rules.map((rule, index) => (
                            <span className="gsc-tool-chip" key={`${ruleLabel(rule)}-${index}`}>
                                <span>{ruleLabel(rule)}</span>
                                <button
                                    type="button"
                                    aria-label={t('tools.pages_queries_changes.rules.remove', {
                                        rule: ruleLabel(rule),
                                    })}
                                    onClick={() => onChange(rules.filter((_, at) => at !== index))}
                                >
                                    <i className="icon-x-close" />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation()(RuleEditor);
